/**
 * The two relink dialogs (M3 plan R16, §2.9 flow 10): the sponsor choice after a successful reboot, and the
 * confirmation before a reboot begins. Pure dialog specs for R1's `showModal`; UI-time only.
 */
import type { FactionsConfig } from '../../../galaxy/layout/config'
import type { FactionKey } from '../../../galaxy/layout/types'
import type { RegionId, Relay, StarSystem } from '../../../galaxy/types'
import type { ReachCore } from '../../runtime/types'
import type { ModalSpec } from '../modal'
import { formatMmSs } from './format'

export interface RelayEnds {
  relay: Relay
  a: StarSystem
  b: StarSystem
}

/** The relay and the two systems it joins, or null for an unknown relay id. */
export function relayEnds(core: ReachCore, relayId: string): RelayEnds | null {
  const relay = core.galaxy.relays.find((r) => r.id === relayId)
  if (!relay) return null
  const a = core.galaxy.systems.find((s) => s.id === relay.a)
  const b = core.galaxy.systems.find((s) => s.id === relay.b)
  return a && b ? { relay, a, b } : null
}

const AUTHORITY: FactionKey = 'authority'

/**
 * The sponsor options: every faction with a presence (a station, or ownership) in either system the relay joins,
 * plus the Authority, deduplicated, in `factions.json` `sponsors` order. Factions outside that list (the Wreckers)
 * never sponsor. The Authority is always offered, first when the list leaves it out.
 */
export function sponsorOptions(core: ReachCore, relayId: string, factions: FactionsConfig): FactionKey[] {
  const present = new Set<FactionKey>([AUTHORITY])
  const ends = relayEnds(core, relayId)
  if (ends) {
    for (const sys of [ends.a, ends.b]) {
      const layout = core.layoutOf(sys.id)
      if (layout.owner !== null) present.add(layout.owner)
      for (const st of layout.stations) present.add(st.faction)
    }
  }
  const out = factions.sponsors.filter((f) => present.has(f))
  if (!out.includes(AUTHORITY)) out.unshift(AUTHORITY)
  return out
}

function relayName(core: ReachCore, relayId: string): string {
  const ends = relayEnds(core, relayId)
  return ends ? `${ends.a.name} ↔ ${ends.b.name}` : relayId
}

/** "Relay ⟨A⟩ ↔ ⟨B⟩ is online. Who sponsors the relink?" with one button per sponsor (ids are faction keys). */
export function sponsorSpec(core: ReachCore, relayId: string, factions: FactionsConfig): ModalSpec {
  const options = sponsorOptions(core, relayId, factions)
  return {
    title: 'Relink complete',
    body: `Relay ${relayName(core, relayId)} is online. Who sponsors the relink?`,
    buttons: options.map((f, i) => ({ id: f, label: factions.factions[f]?.name ?? f, primary: i === 0 })),
    dismissable: false,
  }
}

/** Narrows a sponsor dialog answer to a faction key (answers are always ids from `sponsorSpec`). */
export function isFactionKey(id: string, factions: FactionsConfig): id is FactionKey {
  return factions.sponsors.includes(id as FactionKey) || id === AUTHORITY
}

/**
 * The reboot's defence time for a relay: the live reboot's duration when it is this relay's, otherwise
 * `seconds[region]` for the region of the system the player is in (the relay's own system); null when unknown.
 */
export function rebootSeconds(core: ReachCore, relayId: string, seconds: Partial<Record<RegionId, number>> | null): number | null {
  const layout = core.session.layout
  const site = layout?.relays.find((r) => r.relayId === relayId)
  if (site && core.relink.relayIndex === site.index && core.relink.duration > 0) return core.relink.duration
  if (seconds === null) return null
  let region: RegionId | null = site && layout ? layout.region : null
  if (region === null) {
    const ends = relayEnds(core, relayId)
    region = ends ? ends.a.region : null
  }
  return region === null ? null : (seconds[region] ?? null)
}

export const REBOOT_BEGIN = 'begin'
export const REBOOT_LATER = 'later'

/** "Begin the reboot? Defend the relay for ⟨m:ss⟩." with [Begin] and [Not yet] (Esc = Not yet). */
export function rebootSpec(core: ReachCore, relayId: string, seconds: number | null): ModalSpec {
  const defend = seconds === null ? 'Defend the relay until it comes online.' : `Defend the relay for ${formatMmSs(seconds)}.`
  return {
    title: `Relay ${relayName(core, relayId)}`,
    body: `Begin the reboot? ${defend}`,
    buttons: [
      { id: REBOOT_BEGIN, label: 'Begin', primary: true },
      { id: REBOOT_LATER, label: 'Not yet' },
    ],
  }
}
